import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsNfps.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'invited', label: 'Invited' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

const LAST_LOGIN_OPTIONS = [
  { value: '', label: 'Any last login' },
  { value: 'never', label: 'Never' },
  { value: '30d_plus', label: '30d+' },
];

function formatDateTime(value) {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleString();
}

function roleLabel(role) {
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'executive_leader') return 'Executive Leader';
  if (role === 'ops_manager') return 'Admin';
  if (role === 'ops_viewer') return 'Read-only';
  if (role === 'bd_lead') return 'BD Lead';
  if (role === 'ops_only') return 'Ops Only';
  return role || '—';
}

const COVERAGE_PERMISSION_OPTIONS = [
  {
    key: 'viewCoverageIntelligence',
    effectiveKey: 'canViewCoverageIntelligence',
    label: 'View Loop Health',
    description: 'Allows access to the Loop Health coverage intelligence page and brand-level endpoint.',
  },
  {
    key: 'viewStoreLevelDetail',
    effectiveKey: 'canViewStoreLevelDetail',
    label: 'Store-level detail',
    description: 'Allows named store/location drill-down where available.',
  },
  {
    key: 'exportCoverageData',
    effectiveKey: 'canExportCoverageData',
    label: 'Export coverage data',
    description: 'Allows audited backend CSV exports for categories and merchants.',
  },
];

function canEditCoverageGrants(user) {
  return ['executive_leader', 'ops_manager', 'bd_lead'].includes(user?.role);
}

function structuralCoverageNote(user) {
  if (user?.role === 'super_admin') return 'Super Admin has structural Loop Health access.';
  if (user?.role === 'bd_lead') return 'BD Lead structurally includes view and store-level detail; export remains grantable.';
  if (user?.role === 'ops_viewer') return 'Read-only users cannot receive Loop Health coverage grants.';
  return '';
}

function userStatusTone(status) {
  if (status === 'active') return 'good';
  if (status === 'disabled') return 'bad';
  return '';
}

function Drawer({ open, children, onClose }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: open ? 'auto' : 'none',
        background: open ? 'rgba(15, 23, 42, 0.24)' : 'transparent',
        transition: 'background 160ms ease',
        zIndex: 900,
      }}
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: 'min(460px, 100%)',
          background: '#fff',
          boxShadow: open ? '-18px 0 40px rgba(15, 23, 42, 0.12)' : 'none',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 180ms ease',
          padding: 20,
          overflow: 'auto',
        }}
      >
        {children}
      </aside>
    </div>
  );
}

function AddUserModal({ open, form, saving, result, roleOptions, onChange, onClose, onSubmit, onCopy }) {
  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add User"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(640px, 100%)', background: '#fff', borderRadius: 18, padding: 20 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h3 className="ops-section-title" style={{ margin: 0 }}>Add User</h3>
          <button type="button" className="ops-btn" onClick={onClose}>Close</button>
        </div>
        {result ? (
          <div style={{ marginTop: 16 }}>
            <div className="ops-message">Invite created.</div>
            <div className="ops-kv" style={{ marginTop: 12 }}>
              <div className="k">Invite link</div>
              <div className="v" style={{ wordBreak: 'break-all' }}>{result.resetLink || 'No link available'}</div>
            </div>
            <div className="ops-kv">
              <div className="k">Expires</div>
              <div className="v">{result.expiresAt ? formatDateTime(result.expiresAt) : '—'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button type="button" className="ops-btn" onClick={() => onCopy(result.resetLink)}>Copy invite link</button>
              <button type="button" className="ops-btn primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <div className="ops-action-form-fields" style={{ marginTop: 16 }}>
            <div className="ops-nfp-field">
              <label>Email</label>
              <input
                title="Enter the login email."
                value={form.email}
                onChange={(e) => onChange({ email: e.target.value })}
              />
            </div>
            <div className="ops-nfp-field">
              <label>Full name</label>
              <input
                title="Enter the display name."
                value={form.fullName}
                onChange={(e) => onChange({ fullName: e.target.value })}
              />
            </div>
            <div className="ops-nfp-field">
              <label>Role</label>
              <select title="Choose the initial role." value={form.role} onChange={(e) => onChange({ role: e.target.value })}>
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }} title="Email the one-time link.">
              <input
                type="checkbox"
                checked={form.sendInviteEmail}
                onChange={(e) => onChange({ sendInviteEmail: e.target.checked })}
              />
              Send invite email
            </label>
            {form.error ? <div className="ops-message error">{form.error}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="ops-btn" title="Close this dialog." onClick={onClose}>Cancel</button>
              <button type="button" className="ops-btn primary" disabled={saving} onClick={onSubmit}>
                {saving ? 'Creating…' : 'Create invite'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OpsUsers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [roleValues, setRoleValues] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [lastLoginFilter, setLastLoginFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    email: '',
    fullName: '',
    role: 'ops_manager',
    sendInviteEmail: true,
    error: '',
  });
  const [addResult, setAddResult] = useState(null);
  const [actionState, setActionState] = useState({ loading: false, error: '', success: '', resetLink: '' });

  async function loadUsers() {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/ops/users');
      const users = Array.isArray(data?.users) ? data.users : [];
      setRows(users);
      setRoleValues(Array.isArray(data?.roles) ? data.roles : ['super_admin', 'executive_leader', 'ops_manager', 'ops_viewer']);
      if (!selectedUserId && users.length) setSelectedUserId(users[0].id);
      if (selectedUserId && !users.some((row) => row.id === selectedUserId)) {
        setSelectedUserId(users[0]?.id || '');
      }
    } catch (err) {
      if (err?.status === 401) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      if (err?.status === 403) {
        navigate('/ops', { replace: true });
        return;
      }
      setError(err?.data?.error || err?.message || 'Failed to load users');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roleOptions = useMemo(
    () => roleValues.map((value) => ({ value, label: roleLabel(value) })),
    [roleValues]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
    return rows.filter((row) => {
      if (q) {
        const hay = `${row.fullName || ''} ${row.email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (roleFilter && row.role !== roleFilter) return false;
      if (statusFilter && row.status !== statusFilter) return false;
      if (lastLoginFilter === 'never' && row.lastLoginAt) return false;
      if (lastLoginFilter === '30d_plus') {
        if (!row.lastLoginAt) return false;
        const ts = new Date(row.lastLoginAt).getTime();
        if (!Number.isFinite(ts) || ts > cutoff) return false;
      }
      return true;
    });
  }, [lastLoginFilter, roleFilter, rows, search, statusFilter]);

  const selectedUser = useMemo(
    () => rows.find((row) => row.id === selectedUserId) || null,
    [rows, selectedUserId]
  );

  async function copyText(value) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setActionState((prev) => ({ ...prev, success: 'Copied', error: '' }));
    } catch (_err) {
      setActionState((prev) => ({ ...prev, error: 'Clipboard copy failed', success: '' }));
    }
  }

  async function handleAddUser() {
    const payload = {
      email: addForm.email.trim(),
      fullName: addForm.fullName.trim(),
      role: addForm.role,
      sendInviteEmail: !!addForm.sendInviteEmail,
    };
    if (!payload.email || !payload.fullName || !payload.role) {
      setAddForm((prev) => ({ ...prev, error: 'Email, full name, and role are required.' }));
      return;
    }
    setSaving(true);
    setAddForm((prev) => ({ ...prev, error: '' }));
    try {
      const data = await apiFetch('/ops/users', { method: 'POST', body: payload });
      setAddResult(data?.invite || null);
      await loadUsers();
    } catch (err) {
      setAddForm((prev) => ({ ...prev, error: err?.data?.error || err?.message || 'Failed to create invite' }));
    } finally {
      setSaving(false);
    }
  }

  function resetModal() {
    setModalOpen(false);
    setAddResult(null);
    setAddForm({ email: '', fullName: '', role: roleValues.includes('ops_manager') ? 'ops_manager' : (roleValues[0] || ''), sendInviteEmail: true, error: '' });
  }

  async function patchUser(userId, patch) {
    setActionState({ loading: true, error: '', success: '', resetLink: '' });
    try {
      const data = await apiFetch(`/ops/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: patch,
      });
      setActionState({ loading: false, error: '', success: 'Saved', resetLink: '' });
      const updated = data?.user || null;
      setRows((prev) => prev.map((row) => (row.id === userId && updated ? updated : row)));
    } catch (err) {
      setActionState({ loading: false, error: err?.data?.error || err?.message || 'Update failed', success: '', resetLink: '' });
    }
  }

  function patchCoveragePermission(user, key, checked) {
    if (!user) return;
    const current = user.coveragePermissions || {};
    const next = {
      viewCoverageIntelligence: !!current.viewCoverageIntelligence,
      viewStoreLevelDetail: !!current.viewStoreLevelDetail,
      exportCoverageData: !!current.exportCoverageData,
      [key]: checked,
    };
    if (next.viewStoreLevelDetail || next.exportCoverageData) {
      next.viewCoverageIntelligence = true;
    }
    patchUser(user.id, { coveragePermissions: next });
  }

  async function issueReset(user) {
    if (!user) return;
    setActionState({ loading: true, error: '', success: '', resetLink: '' });
    try {
      const data = await apiFetch(`/ops/users/${encodeURIComponent(user.id)}/reset-password`, {
        method: 'POST',
        body: { sendEmail: true },
      });
      setActionState({
        loading: false,
        error: '',
        success: data?.mode === 'invite' ? 'Invite sent' : 'Reset link sent',
        resetLink: data?.resetLink || '',
      });
    } catch (err) {
      setActionState({ loading: false, error: err?.data?.error || err?.message || 'Failed to issue reset', success: '', resetLink: '' });
    }
  }

  async function disableOrRemove(user, hard = false) {
    if (!user) return;
    setActionState({ loading: true, error: '', success: '', resetLink: '' });
    try {
      const method = hard ? 'DELETE' : 'PATCH';
      const path = hard ? `/ops/users/${encodeURIComponent(user.id)}?mode=hard` : `/ops/users/${encodeURIComponent(user.id)}`;
      const body = hard ? undefined : { status: user.status === 'disabled' ? 'active' : 'disabled' };
      await apiFetch(path, { method, body });
      setActionState({
        loading: false,
        error: '',
        success: hard ? 'User removed' : (user.status === 'disabled' ? 'User enabled' : 'User disabled'),
        resetLink: '',
      });
      await loadUsers();
    } catch (err) {
      setActionState({ loading: false, error: err?.data?.error || err?.message || 'Action failed', success: '', resetLink: '' });
    }
  }

  return (
    <OpsLayout
      title="Users"
      subtitle="Manage internal Ops access. Disable is the default off-ramp; hard remove is restricted to inactive invites."
      headerActions={<button type="button" className="ops-btn primary" onClick={() => setModalOpen(true)}>+ Add User</button>}
    >
      <div className="ops-nfp-content">
        <section className="ops-nfp-panel" style={{ marginTop: 0 }}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '2fr 1fr 1fr 1fr auto' }}>
            <div className="ops-nfp-field" style={{ margin: 0 }}>
              <label>Search</label>
              <input
                title="Filter by name or email."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Email or name"
              />
            </div>
            <div className="ops-nfp-field" style={{ margin: 0 }}>
              <label>Role</label>
              <select title="Filter by role." value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="">All roles</option>
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="ops-nfp-field" style={{ margin: 0 }}>
              <label>Status</label>
              <select title="Filter by status." value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="ops-nfp-field" style={{ margin: 0 }}>
              <label>Last login</label>
              <select title="Filter by last login." value={lastLoginFilter} onChange={(e) => setLastLoginFilter(e.target.value)}>
                {LAST_LOGIN_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                className="ops-btn"
                title="Clear all filters."
                onClick={() => {
                  setSearch('');
                  setRoleFilter('');
                  setStatusFilter('');
                  setLastLoginFilter('');
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </section>

        <section className="ops-nfp-panel">
          {loading ? <div>Loading users…</div> : null}
          {!loading && error ? <div className="ops-message error">{error}</div> : null}
          {!loading && !error ? (
            <div className="ops-nfp-table-wrap">
              <table className="ops-nfp-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>User Since</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7}>No users matched this filter.</td>
                    </tr>
                  ) : filteredRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedUserId(row.id)}
                      style={{ cursor: 'pointer', background: row.id === selectedUserId ? '#f8fafc' : undefined }}
                    >
                      <td>{row.fullName || '—'}</td>
                      <td>{row.email || '—'}</td>
                      <td>{roleLabel(row.role)}</td>
                      <td><span className={`ops-chip ${userStatusTone(row.status)}`}>{row.status}</span></td>
                      <td>{formatDateTime(row.userSince)}</td>
                      <td>{row.lastLoginAt ? formatDateTime(row.lastLoginAt) : 'Never'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="ops-btn" onClick={(e) => { e.stopPropagation(); setSelectedUserId(row.id); }}>
                            View
                          </button>
                          <button type="button" className="ops-btn" title={row.status === 'invited' ? 'Send a new invite link.' : 'Send a password reset link.'} onClick={(e) => { e.stopPropagation(); issueReset(row); }}>
                            {row.status === 'invited' ? 'Resend Invite' : 'Reset'}
                          </button>
                          <button
                            type="button"
                            className="ops-btn"
                            title={row.status === 'disabled' ? 'Re-enable this user.' : 'Disable this user.'}
                            onClick={(e) => { e.stopPropagation(); patchUser(row.id, { status: row.status === 'disabled' ? 'active' : 'disabled' }); }}
                          >
                            {row.status === 'disabled' ? 'Enable' : 'Disable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>

      <Drawer open={!!selectedUser} onClose={() => setSelectedUserId('')}>
        {selectedUser ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 className="ops-section-title" style={{ margin: 0 }}>{selectedUser.fullName}</h3>
              <button type="button" className="ops-btn" onClick={() => setSelectedUserId('')}>Close</button>
            </div>

            <section className="ops-nfp-panel" style={{ marginTop: 16 }}>
              <h4 style={{ marginTop: 0 }}>Identity</h4>
              <div className="ops-kv"><div className="k">Name</div><div className="v">{selectedUser.fullName || '—'}</div></div>
              <div className="ops-kv"><div className="k">Email</div><div className="v">{selectedUser.email || '—'}</div></div>
              <div className="ops-kv"><div className="k">Role</div><div className="v">{roleLabel(selectedUser.role)}</div></div>
              <div className="ops-kv"><div className="k">Status</div><div className="v"><span className={`ops-chip ${userStatusTone(selectedUser.status)}`}>{selectedUser.status}</span></div></div>
            </section>

            <section className="ops-nfp-panel">
              <h4 style={{ marginTop: 0 }}>Metadata</h4>
              <div className="ops-kv"><div className="k">User since</div><div className="v">{formatDateTime(selectedUser.userSince)}</div></div>
              <div className="ops-kv"><div className="k">Last login</div><div className="v">{selectedUser.lastLoginAt ? formatDateTime(selectedUser.lastLoginAt) : 'Never'}</div></div>
              <div className="ops-kv"><div className="k">Created by</div><div className="v">{selectedUser.createdByName || '—'}</div></div>
              <div className="ops-kv"><div className="k">Last role change</div><div className="v">{selectedUser.lastRoleChangeAt ? formatDateTime(selectedUser.lastRoleChangeAt) : '—'}</div></div>
            </section>

            <section className="ops-nfp-panel">
              <h4 style={{ marginTop: 0 }}>Actions</h4>
              <div className="ops-action-form-fields">
                <div className="ops-nfp-field">
                  <label>Change role</label>
                  <select
                    title="Change this user role."
                    value={selectedUser.role}
                    onChange={(e) => patchUser(selectedUser.id, { role: e.target.value })}
                    disabled={actionState.loading}
                  >
                    {roleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="ops-btn" title={selectedUser.status === 'invited' ? 'Send a new invite link.' : 'Send a password reset link.'} disabled={actionState.loading} onClick={() => issueReset(selectedUser)}>
                    {selectedUser.status === 'invited' ? 'Resend invite' : 'Reset password'}
                  </button>
                  <button
                    type="button"
                    className="ops-btn"
                    title={selectedUser.status === 'disabled' ? 'Re-enable this user.' : 'Disable this user.'}
                    disabled={actionState.loading}
                    onClick={() => patchUser(selectedUser.id, { status: selectedUser.status === 'disabled' ? 'active' : 'disabled' })}
                  >
                    {selectedUser.status === 'disabled' ? 'Enable user' : 'Disable user'}
                  </button>
                </div>
              </div>
              {actionState.resetLink ? (
                <div style={{ marginTop: 12 }}>
                  <div className="ops-kv">
                    <div className="k">One-time link</div>
                    <div className="v" style={{ wordBreak: 'break-all' }}>{actionState.resetLink}</div>
                  </div>
                  <button type="button" className="ops-btn" onClick={() => copyText(actionState.resetLink)}>Copy link</button>
                </div>
              ) : null}
              {actionState.error ? <div className="ops-message error" style={{ marginTop: 12 }}>{actionState.error}</div> : null}
              {actionState.success ? <div className="ops-message" style={{ marginTop: 12 }}>{actionState.success}</div> : null}
            </section>

            <section className="ops-nfp-panel">
              <h4 style={{ marginTop: 0 }}>Loop Health Access</h4>
              <p style={{ marginTop: 0, color: '#64748b' }}>
                Coverage intelligence grants are separate from mutation roles. Exports are audited server-side.
              </p>
              {structuralCoverageNote(selectedUser) ? (
                <div className="ops-message" style={{ marginBottom: 12 }}>{structuralCoverageNote(selectedUser)}</div>
              ) : null}
              <div className="ops-action-form-fields">
                {COVERAGE_PERMISSION_OPTIONS.map((permission) => {
                  const rawChecked = !!selectedUser.coveragePermissions?.[permission.key];
                  const effectiveChecked = !!selectedUser.effectivePermissions?.[permission.effectiveKey];
                  const isBdStructural = selectedUser.role === 'bd_lead' && permission.key !== 'exportCoverageData';
                  const disabled = actionState.loading || selectedUser.role === 'super_admin' || selectedUser.role === 'ops_viewer' || isBdStructural;
                  const checked = selectedUser.role === 'super_admin' || isBdStructural ? true : rawChecked;
                  return (
                    <label
                      key={permission.key}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: '2px 10px',
                        alignItems: 'start',
                        opacity: disabled && selectedUser.role !== 'super_admin' ? 0.68 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => patchCoveragePermission(selectedUser, permission.key, e.target.checked)}
                        style={{ marginTop: 3 }}
                      />
                      <span>
                        <strong>{permission.label}</strong>
                        {effectiveChecked && !rawChecked ? <span className="ops-chip good" style={{ marginLeft: 8 }}>Effective</span> : null}
                        <br />
                        <small style={{ color: '#64748b' }}>{permission.description}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
              {canEditCoverageGrants(selectedUser) ? null : (
                <p style={{ color: '#64748b', fontSize: 12 }}>
                  Change this user to Executive Leader, Admin, or BD Lead to manage explicit coverage grants.
                </p>
              )}
            </section>

            <section className="ops-nfp-panel">
              <h4 style={{ marginTop: 0 }}>Danger Zone</h4>
              <p style={{ marginTop: 0, color: '#64748b' }}>
                Disable is the default removal path. Hard remove is only allowed for never-used invited users.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="ops-btn"
                    title={selectedUser.status === 'disabled' ? 'Re-enable this user.' : 'Disable this user.'}
                    disabled={actionState.loading}
                    onClick={() => disableOrRemove(selectedUser, false)}
                  >
                  {selectedUser.status === 'disabled' ? 'Enable user' : 'Disable user'}
                </button>
                  <button
                    type="button"
                    className="ops-btn"
                    disabled={actionState.loading || !selectedUser.canHardRemove}
                    onClick={() => disableOrRemove(selectedUser, true)}
                    title={selectedUser.canHardRemove ? 'Permanently remove this invited user.' : 'Hard remove is available only for never-used invites.'}
                  >
                    Remove user
                  </button>
              </div>
            </section>
          </div>
        ) : null}
      </Drawer>

      <AddUserModal
        open={modalOpen}
        form={addForm}
        saving={saving}
        result={addResult}
        roleOptions={roleOptions}
        onChange={(patch) => setAddForm((prev) => ({ ...prev, ...patch }))}
        onClose={resetModal}
        onSubmit={handleAddUser}
        onCopy={copyText}
      />
    </OpsLayout>
  );
}
